import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { setOpsCaseStatus } from '../../../requests';
import {
  CASE_ORIGINS,
  createOpsCase,
  escalateOpsCase,
  getOpsCaseFull,
  getOpsCasePage,
  getOpsCasePeople,
  getOpsCaseRecipients,
  getOpsCaseThread,
  relateOpsCase,
  replyOpsCase,
  searchOpsCaseClasses,
  setOpsCaseOwner,
  TOpsCaseFull,
  TOpsCaseMessage,
} from '../../../requests/opsCases';
import AttachmentsPanel from '../AttachmentsPanel';
import CommentThread from '../CommentThread';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Toolbar,
} from '../ui';

const STATUSES = [
  'New',
  'With Support',
  'With MyCSM',
  'With Sales',
  'With Scheduling',
  'Instructor Request',
  'Waiting on Customer',
  'Closed',
];

const timeInStage = (h: number | null) => {
  if (h == null) return '—';
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
};

const when = (iso?: string | null) =>
  iso ? iso.slice(0, 16).replace('T', ' ') : '—';

const selectClass =
  'rounded-md border border-white-two bg-white px-1.5 py-1 text-xs focus:border-blue-primary focus:outline-none';

/**
 * Relate the case to the class it is about. Search by name or a date
 * prefix (2026-10); classes on the case's own account are listed first.
 */
const ClassPicker: React.FC<{
  theCase: TOpsCaseFull;
  busy: boolean;
  onPick: (classId: string) => void;
  onCancel: () => void;
}> = ({ theCase, busy, onPick, onCancel }) => {
  const [search, setSearch] = useState(
    theCase.requested.date ?? theCase.requested.courseName ?? '',
  );
  const [q, setQ] = useState(search.trim());
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const { data: hits = [], isFetching } = useQuery({
    queryKey: ['opsCaseClasses', q, theCase.accountId],
    queryFn: () => searchOpsCaseClasses(q, theCase.accountId),
    enabled: q.length >= 2,
  });
  return (
    <div className="mt-3 rounded-lg border border-white-three bg-grey-50 p-3">
      <label className={labelClass}>Which class is this about?</label>
      <input
        className={`${inputClass} w-full`}
        autoFocus
        placeholder="Class name, or a date like 2026-10-14"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-white-three bg-white">
        {q.length < 2 ? (
          <div className="px-3 py-2 text-xs text-gray-bold">
            Type at least two characters
          </div>
        ) : hits.length === 0 ? (
          <div className="px-3 py-2 text-xs text-gray-bold">
            {isFetching ? 'Searching…' : 'No class matches'}
          </div>
        ) : (
          hits.map((h) => (
            <button
              type="button"
              key={h.id}
              disabled={busy}
              onClick={() => onPick(h.id)}
              className="flex w-full items-center justify-between gap-3 border-b border-white-three px-3 py-2 text-left text-xs last:border-b-0 hover:bg-blue-100/60"
            >
              <span>
                <span className="font-medium text-gray-four">
                  {h.name ?? '(unnamed class)'}
                </span>
                <span className="ml-2 text-gray-bold">
                  {when(h.start)}
                  {h.instructor ? ` · ${h.instructor}` : ''}
                  {h.accountName ? ` · ${h.accountName}` : ''}
                </span>
              </span>
              {h.onAccount && <Pill value="this account" tone="info" />}
            </button>
          ))
        )}
      </div>
      <div className="mt-2 flex justify-end">
        <button type="button" className={btnGhost} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const splitAddresses = (s: string) =>
  s
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean);

const MACHINE_KINDS: Record<string, string> = {
  bounce: 'bounce',
  'auto-reply': 'auto-reply',
  calendar: 'calendar response',
  notification: 'notification',
  marketing: 'marketing',
  staff: 'staff',
  notice: 'portal notice',
};

const messageTone = (m: TOpsCaseMessage) => {
  if (m.direction === 'in')
    return m.kind && m.kind in MACHINE_KINDS && m.kind !== 'notice'
      ? ('warn' as const)
      : ('info' as const);
  if (m.status === 'sent') return 'good' as const;
  if (m.status === 'failed') return 'bad' as const;
  return 'warn' as const;
};

const messageState = (m: TOpsCaseMessage) => {
  if (m.direction === 'in')
    return m.kind && MACHINE_KINDS[m.kind]
      ? `received · ${MACHINE_KINDS[m.kind]}`
      : 'received';
  if (m.status === 'sent')
    return m.via === 'outlook' ? 'sent from Outlook' : 'sent';
  if (m.status === 'drafted') return 'draft in cx@ Outlook';
  if (m.status === 'held')
    return m.delivery === 'draft' ? 'draft · held' : 'held';
  if (m.status === 'failed') return 'failed';
  if (m.status === 'cancelled') return 'cancelled';
  return m.status ?? '';
};

/**
 * The conversation on the case and the reply box. A reply is a draft in
 * the cx@ mailbox by default — finish it in Outlook — or sent from here
 * when the person chooses; either way it is on the case first.
 */
const CaseConversation: React.FC<{
  theCase: TOpsCaseFull;
  onReplied: () => void;
}> = ({ theCase, onReplied }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [thenWaiting, setThenWaiting] = useState(true);
  const [inReplyTo, setInReplyTo] = useState<string | null>(null);

  const { data: thread = [] } = useQuery({
    queryKey: ['opsCaseThread', theCase.id],
    queryFn: () => getOpsCaseThread(theCase.id),
  });
  const { data: who } = useQuery({
    queryKey: ['opsCaseRecipients', theCase.id],
    queryFn: () => getOpsCaseRecipients(theCase.id),
    enabled: open,
  });

  useEffect(() => {
    setOpen(false);
    setTo('');
    setCc('');
    setSubject('');
    setBody('');
    setInReplyTo(null);
  }, [theCase.id]);

  const fill = (which: 'customer' | 'sales' | 'trainer', replace = true) => {
    if (!who) return;
    const add = (list: string, addrs: string[]) =>
      replace
        ? addrs.join(', ')
        : [...splitAddresses(list), ...addrs].join(', ');
    if (which === 'customer') {
      setTo(add(to, who.customer.to));
      setCc(add(cc, who.customer.cc));
    } else if (which === 'sales' && who.salesRep?.email) {
      setTo(add(to, [who.salesRep.email]));
    } else if (which === 'trainer') {
      setTo(
        add(to, who.trainers.map((t) => t.email).filter(Boolean) as string[]),
      );
    }
  };

  const reply = useMutation({
    mutationFn: (delivery: 'draft' | 'send') =>
      replyOpsCase(theCase.id, {
        to: splitAddresses(to),
        cc: splitAddresses(cc),
        subject: subject.trim() || undefined,
        body,
        delivery,
        inReplyTo,
        thenStatus:
          thenWaiting && !theCase.closed ? 'Waiting on Customer' : null,
      }),
    onSuccess: (r) => {
      const what =
        r.status === 'drafted'
          ? 'Draft written to the cx@ mailbox — finish it in Outlook'
          : r.status === 'sent'
          ? 'Sent from cx@'
          : r.status === 'failed'
          ? `Not sent: ${r.error ?? 'transport error'}`
          : `On the case, held in the outbox — ${
              r.holdReason ?? 'mail is not switched on yet'
            }`;
      showToast(what, r.status === 'failed' ? 'error' : 'success');
      setBody('');
      setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['opsCaseThread', theCase.id],
      });
      onReplied();
    },
    onError: (err: unknown) => showToast(resolveApiMessage(err), 'error'),
  });

  const live = who?.transport.mode === 'live' && who.transport.configured;
  const defaultDraft = (who?.replyMode ?? 'draft') === 'draft';
  const canSend = splitAddresses(to).length > 0 && body.trim().length > 0;

  const startReply = (m?: TOpsCaseMessage) => {
    setOpen(true);
    setInReplyTo(m?.direction === 'in' ? m.id : null);
    if (m?.direction === 'in') {
      setTo(m.from ?? '');
      setCc(
        [...m.to, ...m.cc]
          .filter((a) => !(who?.ourMailboxes ?? []).includes(a))
          .join(', '),
      );
    }
  };

  return (
    <Card>
      <div className="px-5 py-4 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div className={labelClass}>Conversation · {thread.length}</div>
          {!open && (
            <button
              className={btnSecondary}
              disabled={theCase.closed}
              title={theCase.closed ? 'Reopen the case to reply' : undefined}
              onClick={() => startReply()}
            >
              Reply
            </button>
          )}
        </div>

        {thread.length === 0 ? (
          <div className="mt-2 text-xs text-gray-bold">
            No messages on this case yet — the intake adds what arrives, and
            replies written here or sent from Outlook on this thread land below.
          </div>
        ) : (
          <ul className="mt-2 grid gap-2">
            {thread.map((m) => (
              <li
                key={m.id}
                className={`rounded-lg border border-white-three p-3 ${
                  m.direction === 'out'
                    ? 'ml-8 bg-blue-100/40'
                    : 'mr-8 bg-grey-50'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-bold">
                  <span>
                    <span className="font-medium text-gray-four">
                      {m.direction === 'in'
                        ? m.fromName ?? m.from ?? 'unknown sender'
                        : m.actorEmail ??
                          (m.via === 'outlook' ? 'Outlook' : 'cx@')}
                    </span>
                    {' → '}
                    {m.to.join(', ') || '—'}
                    {m.cc.length > 0 ? ` · cc ${m.cc.join(', ')}` : ''}
                  </span>
                  <span className="flex items-center gap-2">
                    {when(m.receivedAt ?? m.at)}
                    <Pill value={messageState(m)} tone={messageTone(m)} />
                    {m.webLink && (
                      <a
                        className="text-blue-primary hover:underline"
                        href={m.webLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        open in Outlook ↗
                      </a>
                    )}
                  </span>
                </div>
                {m.subject && (
                  <div className="mt-1 text-xs font-medium text-gray-four">
                    {m.subject}
                  </div>
                )}
                <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap font-sans text-xs text-gray-five">
                  {m.text}
                </pre>
                {m.holdReason && m.status !== 'sent' && (
                  <div className="mt-1 text-[11px] text-gray-bold">
                    {m.holdReason}
                  </div>
                )}
                {m.error && (
                  <div className="mt-1 text-[11px] text-red-600">{m.error}</div>
                )}
                {m.direction === 'in' && !open && !theCase.closed && (
                  <button
                    className={`${btnGhost} mt-1 text-xs`}
                    onClick={() => startReply(m)}
                  >
                    Reply to this
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {open && (
          <div className="mt-3 rounded-lg border border-white-three bg-grey-50 p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-bold">Fill in:</span>
              <button
                className={btnGhost}
                disabled={!who || who.customer.to.length === 0}
                onClick={() => fill('customer')}
                title={who?.customer.to.join(', ')}
              >
                Customer{who?.customer.name ? ` · ${who.customer.name}` : ''}
              </button>
              <button
                className={btnGhost}
                disabled={!who?.salesRep?.email}
                onClick={() => fill('sales')}
                title={
                  who?.salesRep
                    ? who.salesRep.email ?? 'no email on the opportunity'
                    : 'relate the case to a class first'
                }
              >
                Sales rep{who?.salesRep?.name ? ` · ${who.salesRep.name}` : ''}
              </button>
              <button
                className={btnGhost}
                disabled={!who || !who.trainers.some((t) => t.email)}
                onClick={() => fill('trainer')}
                title={
                  who?.trainers.length
                    ? who.trainers
                        .map(
                          (t) => `${t.name}: ${t.email ?? 'no email on file'}`,
                        )
                        .join('\n')
                    : 'relate the case to a class first'
                }
              >
                Trainer
                {who?.trainers.length
                  ? ` · ${who.trainers.map((t) => t.name).join(', ')}`
                  : ''}
              </button>
              {who &&
                who.trainers.length > 0 &&
                !who.trainers.some((t) => t.email) && (
                  <span className="text-gray-bold">
                    (no email on file for the instructor — add it on
                    /ops/instructors)
                  </span>
                )}
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              <div>
                <label className={labelClass}>To</label>
                <input
                  className={`${inputClass} w-full`}
                  placeholder="address, address"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Cc</label>
                <input
                  className={`${inputClass} w-full`}
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-2">
              <label className={labelClass}>Subject</label>
              <input
                className={`${inputClass} w-full`}
                placeholder={`Re: ${
                  theCase.subject ?? ''
                } — the case number is added automatically`}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="mt-2">
              <label className={labelClass}>Message</label>
              <textarea
                className={`${inputClass} min-h-[140px] w-full`}
                autoFocus
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            <label className="mt-2 flex items-center gap-1.5 text-xs text-gray-five">
              <input
                type="checkbox"
                className="ops-check"
                checked={thenWaiting}
                onChange={(e) => setThenWaiting(e.target.checked)}
              />
              then mark the case Waiting on Customer
            </label>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                className={defaultDraft ? btnPrimary : btnSecondary}
                disabled={!canSend || reply.isPending}
                onClick={() => reply.mutate('draft')}
              >
                Save as draft in cx@ Outlook
              </button>
              <button
                className={defaultDraft ? btnSecondary : btnPrimary}
                disabled={!canSend || reply.isPending}
                onClick={() => reply.mutate('send')}
              >
                Send from the portal
              </button>
              <button className={btnGhost} onClick={() => setOpen(false)}>
                Cancel
              </button>
              <span className="text-[11px] text-gray-bold">
                {live
                  ? `From ${who?.transport.from}; a tracked copy goes to the cx@ mailbox.`
                  : 'Mail is not switched on yet: the reply goes on the case and waits in the outbox.'}
              </span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

/** Open a case from a class or an account: the form the ?new=1 link lands on. */
const NewCaseForm: React.FC<{
  classId: string;
  accountId: string;
  subject0: string;
  onDone: (id: string) => void;
  onCancel: () => void;
}> = ({ classId, accountId, subject0, onDone, onCancel }) => {
  const [subject, setSubject] = useState(subject0);
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('New');
  const [priority, setPriority] = useState('Medium');
  const create = useMutation({
    mutationFn: () =>
      createOpsCase({
        subject,
        description: description || null,
        classId: classId || null,
        accountId: accountId || null,
        status,
        priority,
      }),
    onSuccess: (r) => {
      showToast(`Case ${r.caseNumber} opened`, 'success');
      onDone(r.id);
    },
    onError: (err: unknown) => showToast(resolveApiMessage(err), 'error'),
  });
  return (
    <Card>
      <div className="px-5 py-4 text-sm">
        <div className={labelClass}>
          New case
          {classId ? ' on this class' : accountId ? ' on this account' : ''}
        </div>
        <div className="mt-2 grid gap-2 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Subject</label>
            <input
              className={`${inputClass} w-full`}
              autoFocus
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select
              className={`${inputClass} w-full`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.filter((s) => s !== 'Closed').map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Priority</label>
            <select
              className={`${inputClass} w-full`}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {['Low', 'Medium', 'High'].map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-4">
            <label className={labelClass}>Description</label>
            <textarea
              className={`${inputClass} min-h-[90px] w-full`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            className={btnPrimary}
            disabled={subject.trim().length < 3 || create.isPending}
            onClick={() => create.mutate()}
          >
            Open case
          </button>
          <button className={btnGhost} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </Card>
  );
};

const OpsCases: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const caseId = searchParams.get('id') ?? '';
  const newCase = searchParams.get('new') === '1';
  const newClassId = searchParams.get('classId') ?? '';
  const newAccountId = searchParams.get('accountId') ?? '';
  const newSubject = searchParams.get('subject') ?? '';

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [owner, setOwner] = useState('');
  const [origin, setOrigin] = useState('');
  /** '' hides machine mail (bounces, notifications, marketing); 'machine' shows only it; 'all' everything. */
  const [kind, setKind] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  const [picking, setPicking] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    setPicking(false);
    setEscalating(false);
    setReason('');
  }, [caseId]);

  const { data, isLoading } = useQuery({
    queryKey: ['opsCases', q, status, owner, origin, kind, showAll, page],
    queryFn: () =>
      getOpsCasePage({
        q: q || undefined,
        status: status || undefined,
        owner: owner || undefined,
        origin: origin || undefined,
        kind: kind || undefined,
        all: showAll,
        page,
      }),
  });

  const { data: people = [] } = useQuery({
    queryKey: ['opsCasePeople'],
    queryFn: getOpsCasePeople,
  });

  const { data: theCase } = useQuery({
    queryKey: ['opsCase', caseId],
    queryFn: () => getOpsCaseFull(caseId),
    enabled: Boolean(caseId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['opsCases'] });
    queryClient.invalidateQueries({ queryKey: ['opsCase'] });
  };
  const onError = (err: unknown) => showToast(resolveApiMessage(err), 'error');

  const changeStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      setOpsCaseStatus(id, next),
    onSuccess: refresh,
    onError,
  });
  const changeOwner = useMutation({
    mutationFn: ({ id, email }: { id: string; email: string | null }) =>
      setOpsCaseOwner(id, email),
    onSuccess: refresh,
    onError,
  });
  const relate = useMutation({
    mutationFn: ({ id, classId }: { id: string; classId: string | null }) =>
      relateOpsCase(id, { classId }),
    onSuccess: (r) => {
      showToast(
        r.classId ? 'Case related to the class' : 'Link removed',
        'success',
      );
      setPicking(false);
      refresh();
    },
    onError,
  });
  const escalate = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) =>
      escalateOpsCase(id, why),
    onSuccess: () => {
      showToast('Escalated — priority set to High', 'success');
      setEscalating(false);
      setReason('');
      refresh();
    },
    onError,
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const ownerSelect = (id: string, value: string | null) => (
    <select
      className={selectClass}
      value={value ?? ''}
      disabled={changeOwner.isPending}
      onChange={(e) =>
        changeOwner.mutate({ id, email: e.target.value || null })
      }
    >
      <option value="">Unassigned</option>
      {people.map((p) => (
        <option key={p.email} value={p.email}>
          {p.name}
        </option>
      ))}
      {value && !people.some((p) => p.email === value) && (
        <option value={value}>{value}</option>
      )}
    </select>
  );

  return (
    <>
      <PageHeader
        title="CX inbox"
        meta="Route, relate, reply — a reply is a draft in the cx@ mailbox unless you send it from here; the case keeps the thread either way"
      />
      <Toolbar>
        <input
          className={`${inputClass} w-64`}
          placeholder="Search subject…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={inputClass}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Open (any status)</option>
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <select
          className={inputClass}
          value={owner}
          onChange={(e) => {
            setOwner(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Any owner</option>
          <option value="me">Mine</option>
          <option value="none">Unassigned</option>
          {people.map((p) => (
            <option key={p.email} value={p.email}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={origin}
          onChange={(e) => {
            setOrigin(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Any origin</option>
          {CASE_ORIGINS.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
        <select
          className={inputClass}
          value={kind}
          title="Machine mail — bounces, auto-replies, calendar responses, system notifications, marketing — is labelled by the intake and hidden here by default"
          onChange={(e) => {
            setKind(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Hide machine mail</option>
          <option value="machine">Machine mail only</option>
          <option value="staff">Staff mail only</option>
          <option value="all">Everything</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-five">
          <input
            type="checkbox"
            className="ops-check"
            checked={showAll}
            onChange={(e) => {
              setShowAll(e.target.checked);
              setPage(1);
            }}
          />
          Include closed
        </label>
      </Toolbar>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <div className="grid gap-4">
            {newCase && (
              <NewCaseForm
                classId={newClassId}
                accountId={newAccountId}
                subject0={newSubject}
                onDone={(id) => {
                  setSearchParams({ id });
                  refresh();
                }}
                onCancel={() => setSearchParams({})}
              />
            )}
            {caseId && theCase && (
              <Card>
                <div className="px-5 py-4 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 font-semibold text-gray-four">
                        {theCase.subject}
                        {theCase.intakeKind === 'notice' && (
                          <Pill value="Portal notice" tone="info" />
                        )}
                        {theCase.escalatedAt && (
                          <Pill value="Escalated" tone="bad" />
                        )}
                        {theCase.priority === 'High' &&
                          !theCase.escalatedAt && (
                            <Pill value="High priority" tone="warn" />
                          )}
                      </div>
                      <div className="mt-1 text-xs text-gray-three">
                        Case {theCase.caseNumber} · {theCase.origin} ·{' '}
                        {when(theCase.createdDate)} ·{' '}
                        {theCase.contactName ?? 'unknown contact'}
                        {theCase.contactEmail
                          ? ` <${theCase.contactEmail}>`
                          : ''}
                        {theCase.accountName ? (
                          <>
                            {' · '}
                            <Link
                              className="text-blue-primary hover:underline"
                              to={PATH.opsAccountView.replace(
                                ':id',
                                theCase.accountId ?? '',
                              )}
                            >
                              {theCase.accountName}
                            </Link>
                          </>
                        ) : (
                          ' · no account'
                        )}
                      </div>
                    </div>
                    <button
                      className={`${btnGhost} text-xs`}
                      onClick={() => setSearchParams({})}
                    >
                      close
                    </button>
                  </div>

                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <div>
                      <div className={labelClass}>Owner</div>
                      {ownerSelect(theCase.id, theCase.ownerEmail)}
                      {theCase.salesforceOwnerId && (
                        <div className="mt-1 text-[11px] text-gray-bold">
                          Salesforce owner id {theCase.salesforceOwnerId} kept
                          as is
                        </div>
                      )}
                    </div>
                    <div>
                      <div className={labelClass}>Class</div>
                      {theCase.class ? (
                        <div className="text-xs">
                          <Link
                            className="font-medium text-blue-primary hover:underline"
                            to={PATH.opsClassEdit.replace(
                              ':id',
                              theCase.class.id,
                            )}
                          >
                            {theCase.class.name ?? '(unnamed class)'} ↗
                          </Link>
                          <div className="text-gray-bold">
                            {when(theCase.class.start)}
                            {theCase.class.instructor
                              ? ` · ${theCase.class.instructor}`
                              : ''}
                          </div>
                          <div className="mt-1 flex gap-2">
                            <button
                              className={btnGhost}
                              onClick={() => setPicking(true)}
                            >
                              Change
                            </button>
                            <button
                              className={btnGhost}
                              disabled={relate.isPending}
                              onClick={() =>
                                relate.mutate({ id: theCase.id, classId: null })
                              }
                            >
                              Unlink
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs">
                          <span className="text-gray-bold">
                            Not related yet
                          </span>
                          {theCase.requested.courseName && (
                            <div className="text-gray-three">
                              Asked for: {theCase.requested.courseName}
                              {theCase.requested.date
                                ? ` · ${theCase.requested.date}`
                                : ''}
                              {theCase.requested.time
                                ? ` ${theCase.requested.time}`
                                : ''}
                            </div>
                          )}
                          <button
                            className={`${btnSecondary} mt-1`}
                            onClick={() => setPicking(true)}
                          >
                            Relate to a class
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className={labelClass}>Escalation</div>
                      {theCase.escalatedAt ? (
                        <div className="text-xs text-gray-three">
                          Escalated {when(theCase.escalatedAt)} by{' '}
                          {theCase.escalatedBy}
                          {theCase.escalationReason && (
                            <div className="mt-0.5 text-gray-five">
                              {theCase.escalationReason}
                            </div>
                          )}
                        </div>
                      ) : escalating ? (
                        <div className="grid gap-2">
                          <input
                            className={`${inputClass} w-full`}
                            autoFocus
                            placeholder="Why — e.g. program at risk, renewal in 30 days"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                          />
                          <div className="flex gap-2">
                            <button
                              className={btnPrimary}
                              disabled={
                                reason.trim().length < 3 || escalate.isPending
                              }
                              onClick={() =>
                                escalate.mutate({
                                  id: theCase.id,
                                  why: reason.trim(),
                                })
                              }
                            >
                              Escalate
                            </button>
                            <button
                              className={btnGhost}
                              onClick={() => setEscalating(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className={btnSecondary}
                          onClick={() => setEscalating(true)}
                        >
                          Escalate
                        </button>
                      )}
                    </div>
                  </div>

                  {picking && (
                    <ClassPicker
                      theCase={theCase}
                      busy={relate.isPending}
                      onPick={(classId) =>
                        relate.mutate({ id: theCase.id, classId })
                      }
                      onCancel={() => setPicking(false)}
                    />
                  )}

                  {theCase.description && (
                    <pre className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg border border-white-three bg-grey-50 p-3 font-sans text-sm text-gray-five">
                      {theCase.description}
                    </pre>
                  )}

                  {theCase.relatedCases.length > 0 && (
                    <div className="mt-3">
                      <div className={labelClass}>
                        Other cases on this class ·{' '}
                        {theCase.relatedCases.length}
                      </div>
                      <ul className="divide-y divide-white-three rounded-lg border border-white-three text-xs">
                        {theCase.relatedCases.map((c) => (
                          <li key={c.id}>
                            <button
                              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-grey-50"
                              onClick={() => setSearchParams({ id: c.id })}
                            >
                              <span className="truncate">
                                <span className="text-gray-bold">
                                  {c.caseNumber}
                                </span>{' '}
                                <span className="text-gray-four">
                                  {c.subject ?? '(no subject)'}
                                </span>
                              </span>
                              <span className="flex shrink-0 items-center gap-2 text-gray-bold">
                                {when(c.createdDate)}
                                <Pill value={c.status} />
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Card>
            )}
            {caseId && theCase && (
              <>
                <CaseConversation theCase={theCase} onReplied={refresh} />
                <CommentThread recordId={caseId} recordObject="case" compact />
                <AttachmentsPanel recordId={caseId} recordObject="sf_case" />
              </>
            )}

            <Card>
              <div className="overflow-x-auto">
                <table className={`${tableClass} min-w-[1100px]`}>
                  <thead className={theadClass}>
                    <tr>
                      <th className={thClass}>Time in stage</th>
                      <th className={thClass}>Subject</th>
                      <th className={thClass}>Contact</th>
                      <th className={thClass}>Class</th>
                      <th className={thClass}>Owner</th>
                      <th className={thClass}>Opened</th>
                      <th className={thClass}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((c) => (
                      <tr
                        key={c.id}
                        className={`${rowClickClass} ${
                          c.id === caseId ? 'bg-blue-100/60' : ''
                        }`}
                        onClick={() => setSearchParams({ id: c.id })}
                      >
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {timeInStage(c.timeInStageHours)}
                        </td>
                        <td className={`${tdClass} max-w-md`}>
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium text-gray-four">
                              {c.subject ?? '(no subject)'}
                            </span>
                            {c.intakeKind === 'notice' && (
                              <Pill value="Portal notice" tone="info" />
                            )}
                            {c.intakeKind &&
                              c.intakeKind !== 'notice' &&
                              c.intakeKind !== 'customer' && (
                                <Pill value={c.intakeKind} tone="neutral" />
                              )}
                            {c.escalated && (
                              <Pill value="Escalated" tone="bad" />
                            )}
                          </div>
                          <div className="text-[11px] text-gray-bold">
                            {c.origin ?? '—'}
                            {c.reason ? ` · ${c.reason}` : ''}
                          </div>
                        </td>
                        <td className={tdClass}>{c.contactName ?? '—'}</td>
                        <td className={`${tdClass} max-w-[220px]`}>
                          {c.classId ? (
                            <div className="truncate" title={c.className ?? ''}>
                              <span className="text-gray-four">
                                {c.className ?? c.classId}
                              </span>
                              <div className="text-[11px] text-gray-bold">
                                {when(c.classStart)}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-two">—</span>
                          )}
                        </td>
                        <td
                          className={`${tdClass} whitespace-nowrap`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {ownerSelect(c.id, c.ownerEmail)}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {when(c.createdDate)}
                        </td>
                        <td
                          className={`${tdClass} whitespace-nowrap`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <select
                            className={selectClass}
                            value={c.status ?? ''}
                            onChange={(e) =>
                              changeStatus.mutate({
                                id: c.id,
                                next: e.target.value,
                              })
                            }
                          >
                            {STATUSES.map((s) => (
                              <option key={s}>{s}</option>
                            ))}
                            {c.status && !STATUSES.includes(c.status) && (
                              <option value={c.status}>{c.status}</option>
                            )}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data && data.items.length === 0 && (
                <EmptyState
                  title="Nothing in the queue"
                  hint="No cases match these filters."
                />
              )}
              <Pagination
                page={page}
                pages={pages}
                total={data?.total}
                onPage={setPage}
              />
            </Card>
          </div>
        </PageBody>
      )}
    </>
  );
};

export default OpsCases;
